"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  getPublicLegalTerms,
  publicLegalTermsQueryKey,
  publicLegalTermsStaleTime,
  type PublicLegalTerm,
} from "@/shared/api/legal";
import { customerSupportEmail, customerSupportEmailHref } from "@/shared/config/customerSupport";
import {
  legalDocuments,
  type LegalDocumentKind,
  type LegalSection,
} from "@/modules/landing/content/legalDocuments";

type LegalDocumentPageProps = {
  kind: LegalDocumentKind;
};

function normalizeLegalLabel(value?: string | null) {
  return (value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
}

function findPublishedDocument(terms: PublicLegalTerm[], kind: LegalDocumentKind) {
  const orderedTerms = [...terms]
    .filter((term) => term.is_active && term.content.trim())
    .sort((left, right) => {
      const leftTime = Date.parse(left.updated_at || left.created_at || "") || 0;
      const rightTime = Date.parse(right.updated_at || right.created_at || "") || 0;
      return rightTime - leftTime;
    });

  return orderedTerms.find((term) => {
    const category = normalizeLegalLabel(term.category);
    const title = normalizeLegalLabel(term.title);
    const searchable = `${category} ${title}`;

    if (kind === "privacy") {
      return searchable.includes("privacy");
    }

    return !searchable.includes("privacy") && !searchable.includes("provider consent") && (
      category === "terms" ||
      category === "terms and conditions" ||
      searchable.includes("terms and conditions") ||
      searchable.includes("terms of service")
    );
  });
}

function formatPublishedDate(value?: string | null) {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "long", year: "numeric" }).format(parsed);
}

function publishedSections(term: PublicLegalTerm): LegalSection[] {
  return [{
    id: "published-policy",
    title: term.title,
    paragraphs: [term.content.trim()],
  }];
}

export function LegalDocumentPage({ kind }: LegalDocumentPageProps) {
  const definition = legalDocuments[kind];
  const accent = kind === "privacy" ? "#16a878" : "#ff7a1a";
  const PolicyIcon = kind === "privacy" ? ShieldRoundedIcon : GavelRoundedIcon;
  const { data: terms = [], isFetching } = useQuery({
    queryFn: () => getPublicLegalTerms(),
    queryKey: publicLegalTermsQueryKey,
    retry: false,
    staleTime: publicLegalTermsStaleTime,
  });
  const publishedDocument = findPublishedDocument(terms, kind);
  const sections = publishedDocument ? publishedSections(publishedDocument) : definition.sections;
  const publishedDate = formatPublishedDate(publishedDocument?.updated_at || publishedDocument?.created_at);
  const dateLabel = publishedDate || definition.reviewed;

  function handlePrint() {
    const previousTitle = document.title;

    document.title = "\u200B";
    window.addEventListener("afterprint", () => {
      document.title = previousTitle;
    }, { once: true });
    window.print();
  }

  return (
    <Box className="legal-page" component="main" id="legal-page-top" sx={{ bgcolor: "#ffffff", color: "#10182f" }}>
      <Box
        className="legal-page__hero"
        component="section"
        sx={{
          bgcolor: "#071e62",
          borderTop: `4px solid ${accent}`,
          color: "#ffffff",
          overflow: "hidden",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack
            alignItems={{ xs: "flex-start", md: "center" }}
            direction={{ xs: "column", md: "row" }}
            gap={{ xs: 3, md: 4 }}
            justifyContent="space-between"
          >
            <Stack alignItems="flex-start" direction={{ xs: "column", sm: "row" }} gap={{ xs: 2.5, sm: 3 }} sx={{ maxWidth: 820 }}>
              <Box
                className={`legal-page__hero-icon legal-page__hero-icon--${kind}`}
                sx={{
                  alignItems: "center",
                  bgcolor: accent,
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "8px",
                  color: kind === "privacy" ? "#ffffff" : "#071e62",
                  display: "flex",
                  flex: "0 0 auto",
                  height: 64,
                  justifyContent: "center",
                  width: 64,
                }}
              >
                <PolicyIcon sx={{ fontSize: 34 }} />
              </Box>
              <Box>
                <Typography
                  className="legal-page__eyebrow"
                  component="p"
                  sx={{ color: accent, fontSize: 13, fontWeight: 900, letterSpacing: 0, mb: 1, textTransform: "uppercase" }}
                >
                  {definition.eyebrow}
                </Typography>
                <Typography className="legal-page__title" component="h1" sx={{ fontSize: { xs: 34, sm: 42, md: 48 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.08 }}>
                  {definition.title}
                </Typography>
                <Typography className="legal-page__summary" sx={{ color: "rgba(255,255,255,0.78)", fontSize: { xs: 16, md: 18 }, lineHeight: 1.7, mt: 2, maxWidth: 760 }}>
                  {definition.summary}
                </Typography>
              </Box>
            </Stack>

            <Button
              className="legal-page-no-print"
              color="inherit"
              disabled={isFetching}
              onClick={handlePrint}
              startIcon={isFetching ? <SyncRoundedIcon sx={{ animation: "legal-policy-sync 1s linear infinite" }} /> : <PrintRoundedIcon />}
              variant="outlined"
              sx={{
                "@keyframes legal-policy-sync": { to: { transform: "rotate(360deg)" } },
                borderColor: "rgba(255,255,255,0.45)",
                borderRadius: "8px",
                flex: "0 0 auto",
                fontWeight: 800,
                minHeight: 46,
                px: 2.25,
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)", borderColor: "#ffffff" },
                "&.Mui-disabled": { borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.62)" },
              }}
            >
              {isFetching ? "Checking update" : "Print policy"}
            </Button>
          </Stack>

          <Stack className="legal-page__metadata" direction="row" flexWrap="wrap" gap={1.25} sx={{ mt: 4 }}>
            <Chip
              icon={<CalendarMonthRoundedIcon />}
              label={`${publishedDocument ? "Updated" : "Last reviewed"} ${dateLabel}`}
              sx={{ bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)", color: "#ffffff", fontWeight: 750, "& .MuiChip-icon": { color: accent } }}
            />
            {publishedDocument?.version ? (
              <Chip label={`Version ${publishedDocument.version}`} sx={{ bgcolor: accent, color: kind === "privacy" ? "#ffffff" : "#071e62", fontWeight: 850 }} />
            ) : null}
          </Stack>
        </Container>
      </Box>

      <Box className="legal-page__content" component="section" sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              alignItems: "start",
              display: "grid",
              gap: { xs: 4, md: 7 },
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "230px minmax(0, 1fr)" },
            }}
          >
            <Box
              component="aside"
              className="legal-page-no-print"
              sx={{
                bgcolor: "#f3f7fc",
                border: "1px solid #dce6f3",
                borderRadius: "8px",
                p: 2.25,
                position: { md: "sticky" },
                top: { md: 104 },
              }}
            >
              <Typography component="h2" sx={{ color: "#071e62", fontSize: 15, fontWeight: 900, mb: 1.5 }}>
                On this page
              </Typography>
              <Stack component="nav" aria-label={`${definition.title} sections`} spacing={0.25}>
                {sections.map((section) => (
                  <Box
                    component="a"
                    href={`#${section.id}`}
                    key={section.id}
                    sx={{
                      borderLeft: "3px solid transparent",
                      color: "#526078",
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      px: 1.25,
                      py: 0.9,
                      textDecoration: "none",
                      "&:hover, &:focus-visible": { borderLeftColor: accent, color: "#075fc9" },
                    }}
                  >
                    {section.title}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box className="legal-page__article" component="article" sx={{ minWidth: 0 }}>
              {sections.map((section, index) => (
                <Box className="legal-page__section" component="section" id={section.id} key={section.id} sx={{ scrollMarginTop: 112, pb: 4.5, pt: index === 0 ? 0 : 4.5 }}>
                  <Stack className="legal-page__section-heading" alignItems="flex-start" direction="row" gap={2}>
                    <Box aria-hidden="true" sx={{ bgcolor: accent, borderRadius: "2px", flex: "0 0 auto", height: 28, mt: 0.25, width: 4 }} />
                    <Typography component="h2" sx={{ color: "#071e62", fontSize: { xs: 23, md: 27 }, fontWeight: 900, letterSpacing: 0, lineHeight: 1.25 }}>
                      {section.title}
                    </Typography>
                  </Stack>
                  <Stack className="legal-page__paragraphs" spacing={2} sx={{ mt: 2.25 }}>
                    {section.paragraphs.map((paragraph, paragraphIndex) => (
                      <Typography
                        key={`${section.id}-${paragraphIndex}`}
                        sx={{ color: "#4e596d", fontSize: 16, lineHeight: 1.85, overflowWrap: "anywhere", whiteSpace: publishedDocument ? "pre-wrap" : "normal" }}
                      >
                        {paragraph}
                      </Typography>
                    ))}
                  </Stack>
                  {section.bullets?.length ? (
                    <Box className="legal-page__bullets" component="ul" sx={{ color: "#4e596d", display: "grid", gap: 1.2, mb: 0, mt: 2.25, pl: 3 }}>
                      {section.bullets.map((bullet) => (
                        <Typography component="li" key={bullet} sx={{ fontSize: 16, lineHeight: 1.7, pl: 0.5 }}>
                          {bullet}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                  {index < sections.length - 1 ? <Divider sx={{ borderColor: "#e3e9f1", mt: 4.5 }} /> : null}
                </Box>
              ))}

              <Box
                className="legal-page-no-print"
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  bgcolor: "#f3f7fc",
                  border: "1px solid #dce6f3",
                  borderLeft: `4px solid ${accent}`,
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: "space-between",
                  mt: 2,
                  p: { xs: 2.5, sm: 3 },
                }}
              >
                <Box>
                  <Typography sx={{ color: "#071e62", fontSize: 18, fontWeight: 900 }}>Questions about this policy?</Typography>
                  <Typography sx={{ color: "#59657a", lineHeight: 1.6, mt: 0.5 }}>Our support team can help at {customerSupportEmail}.</Typography>
                </Box>
                <Button
                  component="a"
                  href={customerSupportEmailHref}
                  startIcon={<MailOutlineRoundedIcon />}
                  variant="contained"
                  sx={{ bgcolor: "#075fc9", borderRadius: "8px", boxShadow: "none", flex: "0 0 auto", fontWeight: 850, minHeight: 44, px: 2.25, "&:hover": { bgcolor: "#064fa8", boxShadow: "none" } }}
                >
                  Email support
                </Button>
              </Box>

              <Button
                className="legal-page-no-print"
                component={Link}
                href="#legal-page-top"
                startIcon={<ArrowUpwardRoundedIcon />}
                sx={{ color: "#075fc9", fontWeight: 850, mt: 3 }}
              >
                Back to top
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
